import type { ChartDatapoint } from '../../../core/types/chart-data.js';
import { DEPTH_FIELD } from '../constants.js';

type OrderingAccessors = {
  spanIdAccessor: string;
  parentIdAccessor: string;
  x0Accessor: string;
  x1Accessor: string;
};

type Entry = {
  span: ChartDatapoint;
  index: number;
  x0: number;
};

type Frame = { span: ChartDatapoint; depth: number };

/**
 * Traverses the span forest in DFS pre-order (parent before children, siblings
 * sorted by `x0` with original index as tie-breaker) and returns a flat list
 * where each span carries its `depth`. Zero-width spans are widened by 1 so
 * they remain paintable on the canvas.
 *
 * Numeric x0/x1 are parsed once per span and cached on the internal `Entry`,
 * so the sort comparator does not re-run `Number()` at every comparison.
 * Traversal is iterative to avoid stack overflows on deep flame graphs.
 */
export function orderSpansHierarchically(
  spans: ChartDatapoint[],
  {
    spanIdAccessor,
    parentIdAccessor,
    x0Accessor,
    x1Accessor,
  }: OrderingAccessors,
): ChartDatapoint[] {
  const spanIds = new Set<unknown>();
  for (const span of spans) {
    spanIds.add(span[spanIdAccessor]);
  }

  const childrenMap = new Map<unknown, Entry[]>();
  const roots: Entry[] = [];

  for (let index = 0; index < spans.length; index++) {
    const span = spans[index];
    const entry: Entry = {
      span,
      index,
      x0: Number(span[x0Accessor]),
    };
    const parentId = span[parentIdAccessor];
    const isRoot =
      parentId === null || parentId === undefined || !spanIds.has(parentId);

    if (isRoot) {
      roots.push(entry);
      continue;
    }

    const siblings = childrenMap.get(parentId);
    if (siblings) {
      siblings.push(entry);
    } else {
      childrenMap.set(parentId, [entry]);
    }
  }

  const byX0AndIndex = (a: Entry, b: Entry) => a.x0 - b.x0 || a.index - b.index;

  roots.sort(byX0AndIndex);
  for (const children of childrenMap.values()) {
    children.sort(byX0AndIndex);
  }

  const result: ChartDatapoint[] = [];
  const stack: Frame[] = [];
  for (let i = roots.length - 1; i >= 0; i--) {
    stack.push({ span: roots[i].span, depth: 0 });
  }

  for (let frame = stack.pop(); frame !== undefined; frame = stack.pop()) {
    const { span, depth } = frame;
    const x0 = Number(span[x0Accessor]);
    const x1 = Number(span[x1Accessor]);
    const widened = x0 === x1 ? { [x1Accessor]: x1 + 1 } : undefined;
    result.push({ ...span, ...widened, [DEPTH_FIELD]: depth });

    const children = childrenMap.get(span[spanIdAccessor]);
    if (!children) {
      continue;
    }
    for (let i = children.length - 1; i >= 0; i--) {
      stack.push({ span: children[i].span, depth: depth + 1 });
    }
  }

  return result;
}
